<template>
  <div class="d-flex justify-content-end">
    <McTableFilter
      ref="tableFilter"
      :activeFilters="localFilters"
      :parseFilterDataMappings="parseFilterDataMappings"
      :isDisabled="loading"
      :presetFilters="savedFilters"
      :presetFullObjects="savedFullObjects"
      hasSaveFilter
      :defaultValues="{
        motionStatus: ['M', 'I', 'A', 'P', 'O'],
        assignmentType: 'all',
      }"
      @submit="handleInput"
      @reset="handleReset"
      @setFilter="handleSetFilter"
      @saveFilter="handleSaveFilter"
      @deleteFilter="handleDeleteFilter"
    >
      <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
        <div class="mc-modal__filter-item">
          <label class="formulate-input-label">{{ $t('global.object.fleetStatus') }}</label>
          <div class="mc-modal__filter-button-group">
            <template v-for="item in vehicleStatusOptions">
              <button
                :key="`${item.key}-${btnKey}`"
                type="button"
                :class="{ 'mc-modal__filter-button--active': motionStatus[item.key] }"
                class="mc-modal__filter-button"
                @click="
                  filterStatus(item.key)
                  updateFormValues({ key: 'motionStatus', value: getMotionStatusArray() })
                "
              >
                {{ item.label }}
              </button>
            </template>
          </div>
          <FormulateInput type="hidden" name="motionStatus" :value="formValues.motionStatus" />
        </div>
        <div class="mc-modal__filter-item">
          <RadioButtonWrapper
            :field="assignmentFields"
            :value="formValues.assignmentType || assignmentFields.default"
            @input="updateFormValues({ key: 'assignmentType', value: $event })"
          />
        </div>
        <div class="mc-modal__filter-item">
          <FormulateInput
            v-bind="vehicleFields"
            :fullObject="fullObjects['licensePlate'] || []"
            :value="formValues.licensePlate"
            @update:formValues="updateFormValues({ key: 'licensePlate', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'licensePlate', value: $event })"
          />
        </div>
      </template>
    </McTableFilter>

    <b-modal
      id="modal-edit-filter"
      :title="
        savedFilters.length > 0
          ? $t('controlTower.filter.editPresetFilter')
          : $t('controlTower.filter.presetNotFound')
      "
      size="md"
      centered
      hide-header-close
      no-close-on-esc
    >
      <TableFilter
        v-if="savedFilters.length > 0"
        :fields="modalPresetFields"
        :subFields="modalPresetSubFields"
        :savedFilters="savedFilters"
        :vehicles="vehicles.vehicles"
        @selected-rows="updateSelectedRow"
      />
      <div v-else>
        {{ $t('controlTower.filter.noPresetDesc') }}
      </div>
      <template #modal-footer="{ cancel }">
        <McButton
          variant="secondary"
          :text="$t('global.action.close')"
          class="me-2"
          @click="cancel"
        />
        <McButton
          v-if="savedFilters.length > 0"
          variant="primary"
          :text="$t('videoHistory.updateFilter')"
          @click="handleUpdateFilter"
        />
        <McButton
          v-if="savedFilters.length == 0"
          variant="primary"
          :text="$t('controlTower.filter.saveNew')"
          class="me-2"
          @click="openModalCreate"
        />
      </template>
    </b-modal>
  </div>
</template>
<script>
import McButton from '@/components/McButton'
import McTableFilter from '@/components/McTableFilter'
import { MixPanel } from '@/mixin/mixpanel'
import {
  CREATE_USER_PREFERENCES,
  DELETE_USER_PREFERENCES,
  GET_USER_PREFERENCES,
  UPDATE_USER_PREFERENCES,
} from '@/services/api/controltower'
import {
  GET_VEHICLE,
  GET_VEHICLE_FILTER,
  GET_VEHICLE_GROUPS,
  GET_VEHICLE_TYPES,
} from '@/services/api/master/fleet'
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import TableFilter from '../component/TableFilter.vue'

import RadioButtonWrapper from '../wrapper/RadioButtonWrapper'

export default {
  components: {
    McTableFilter,
    TableFilter,
    McButton,
    RadioButtonWrapper,
  },
  mixins: [MixPanel],
  apollo: {
    vehicleTypes: {
      query: GET_VEHICLE_TYPES,
      variables() {
        return {
          filter: {
            hasVehicle: true,
            isActive: true,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        let vehicleTypes = { ...data.vehicleTypes }
        this.$store.commit('report/updateVehicleTypeList', {
          list: vehicleTypes.vehicleTypes.data,
          loaded: true,
        })
      },
    },
    vgroups: {
      query: GET_VEHICLE_GROUPS,
      result({ data, loading }) {
        if (loading) return
        let vgroups = { ...data.vgroups }
        this.$store.commit('report/updateVgroupList', {
          list: vgroups.vgroups,
          loaded: true,
        })
      },
    },
    vehicles: {
      query: GET_VEHICLE,
      variables() {
        return {
          filter: {
            status: ['I', 'R', 'A'],
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        this.vehicles = data.vehicles || { vehicles: [] }
      },
    },
    userPreferences: {
      query: GET_USER_PREFERENCES,
      fetchPolicy: 'cache-and-network',
      result({ data, loading }) {
        if (loading) return
        this.savedFilters = data.userPreferences.userPreferences
      },
    },
  },
  props: {
    activeFilters: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:fullObject'],
  data() {
    return {
      localFilters: {},
      motionStatus: {
        all: true,
      },
      btnKey: 0,
      savedFilters: [],
      savedFullObjects: {
        licensePlate: [],
      },
      vehicles: {
        vehicles: [],
      },
      vehicleTypes: {
        vehicleTypes: [],
      },
      vgroups: {
        vgroups: [],
      },
      selectedRows: {
        label: '',
        value: {},
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    vehicleStatus() {
      return [
        {
          key: 'M',
          label: this.$t('liveView.filter.moving'),
          scope: null,
        },
        {
          key: 'I',
          label: this.$t('liveView.filter.idle'),
          scope: null,
        },
        {
          key: 'A',
          label: this.$t('liveView.filter.assisted'),
          scope: this.hasAccess(['assisted_motion_status:read']),
        },
        {
          key: 'P',
          label: this.$t('liveView.filter.parking'),
          scope: null,
        },
        {
          key: 'O',
          label: this.$t('liveView.filter.offline'),
          scope: null,
        },
      ]
    },
    vehicleStatusOptions() {
      const data = this.vehicleStatus.filter((item) => item.scope === true || item.scope === null)
      data.unshift({
        key: 'all',
        label: this.$t('global.filter.all'),
        scope: null,
      })
      return data
    },
    vehicleFields() {
      const tableFields = [
        {
          label: this.$t('report.fields.licensePlate'),
          key: 'licensePlate',
          isSortable: true,
        },
        {
          label: this.$t('report.fields.vehicleType'),
          key: 'vehicleType',
          isSortable: false,
          formatter: (value) => {
            return value
              ? `${value.manufacturer ?? '-'} / ${value.model ?? '-'} / ${value.type ?? '-'} `
              : '-'
          },
        },
        {
          label: this.$t('report.fields.vehicleGroup'),
          key: 'vehicle2Vgroup',
          isSortable: false,
          formatter: (value) => {
            return value && value.length > 0 ? value.map((v) => v.vgroup.name).join(', ') : '-'
          },
        },
      ]
      return {
        name: 'licensePlate',
        isShow: true,
        label: this.$t('report.fields.licensePlate'),
        type: 'report-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('report.fields.licensePlate')}`,
        trackBy: 'id',
        displayedBy: 'licensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        multiple: true,
        tableFields: tableFields,
        filters: [
          {
            label: this.$t('report.fields.vehicleType'),
            key: 'vehicleType',
            data: this.getFilterValue(1000, 'vehicleType'),
            model: [],
          },
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vgroup',
            data: this.getFilterValue(1000, 'vgroup'),
            model: [],
          },
        ],
      }
    },
    vgroupList() {
      return this.$store.state.report.vGroupList
    },
    vehicleTypeList() {
      return this.$store.state.report.vehicleTypeList
    },
    parseFilterDataMappings() {
      return {
        motionStatus: 'motionStatus',
        licensePlate: 'vehicleId',
        assignmentType: 'assignmentType',
      }
    },
    modalPresetFields() {
      return [
        { key: 'preset', label: this.$t('controlTower.filter.presetName'), rowspan: 2 },
        {
          key: 'parameter',
          label: this.$t('controlTower.filter.parameter'),
          colspan: 2,
        },
      ]
    },

    modalPresetSubFields() {
      return [
        { key: 'label', label: this.$t('global.field.name'), thClass: 'd-none' },
        { key: 'status', label: 'Status' },
        { key: 'totalVehicle', label: this.$t('controlTower.filter.fleetAmount') },
      ]
    },

    assignmentFields() {
      return {
        name: 'assignmentType',
        label: this.$t('controlTower.detail.fleetTask'),
        type: 'radio-button',
        default: 'all',
        options: [
          {
            key: 'all',
            label: this.$t('global.filter.all'),
          },
          {
            key: 'assigned',
            label: this.$t('controlTower.filter.assigned'),
          },
          {
            key: 'unassigned',
            label: this.$t('controlTower.filter.unassigned'),
          },
        ],
      }
    },
  },
  watch: {
    activeFilters: {
      handler(newValue) {
        if (newValue) {
          this.setLocalFilters(newValue)
        }
      },
      deep: true,
    },
    'vehicles.vehicles': {
      handler() {
        this.syncSavedFullObjects()
      },
      deep: true,
    },
    'localFilters.vehicleId': {
      handler() {
        this.syncSavedFullObjects()
      },
      deep: true,
    },
  },
  methods: {
    setLocalFilters(filters) {
      if (!filters) return
      this.localFilters = cloneDeep(filters)
      if (filters.motionStatus && Array.isArray(filters.motionStatus)) {
        this.convertMotionStatusArrayToObject(filters.motionStatus)
      }
    },
    syncSavedFullObjects() {
      const vehicleIds = this.localFilters?.vehicleId || []
      const vehicles = this.vehicles?.vehicles || []

      this.$set(
        this.savedFullObjects,
        'licensePlate',
        vehicles.filter((item) => Array.isArray(vehicleIds) && vehicleIds.includes(item.id))
      )
    },
    convertMotionStatusArrayToObject(motionStatusArray) {
      const availableStatus = this.vehicleStatusOptions.filter((i) => i.key !== 'all')

      if (!Array.isArray(motionStatusArray) || motionStatusArray.length === 0) {
        this.motionStatus = availableStatus.reduce((acc, item) => {
          acc[item.key] = false
          return acc
        }, {})
        this.motionStatus.all = true
        this.btnKey += 1
        return
      }

      const tempStatus = availableStatus.reduce((acc, item) => {
        acc[item.key] = motionStatusArray.includes(item.key)
        return acc
      }, {})

      const activeCount = Object.values(tempStatus).filter(Boolean).length
      const isAll = activeCount === availableStatus.length || activeCount === 0

      if (isAll) {
        // Jika semua terpilih, reset status individu ke false dan set 'all' ke true
        this.motionStatus = availableStatus.reduce((acc, item) => {
          acc[item.key] = false
          return acc
        }, {})
        this.motionStatus.all = true
      } else {
        // Jika hanya sebagian, gunakan status individu dan set 'all' ke false
        this.motionStatus = { ...tempStatus, all: false }
      }

      this.btnKey += 1
    },
    getMotionStatusArray() {
      if (this.motionStatus.all) {
        return this.vehicleStatus.map((item) => item.key)
      }
      return Object.keys(this.motionStatus).filter((key) => this.motionStatus[key] && key !== 'all')
    },
    getListFilterLoaded(type) {
      let loaded = false
      let list = []
      switch (type) {
        case 'vehicleType':
          list = this.vehicleTypeList.list.map((item) => {
            return {
              id: item.vehicleTypeId,
              name: `${item.manufacturer} / ${item.model} / ${item.type}`,
            }
          })
          loaded = this.vehicleTypeList.loaded
          break
        case 'vgroup':
          list = this.vgroupList.list.map((item) => {
            return { id: item.id, name: item.name }
          })
          loaded = this.vgroupList.loaded
          break
        default:
          break
      }
      return { loaded, list }
    },
    getFilterValue(interval, type) {
      return new Promise((resolve) => {
        const { loaded, list } = this.getListFilterLoaded(type)
        if (!loaded) {
          const intervalId = setInterval(() => {
            const { loaded, list } = this.getListFilterLoaded(type)
            if (loaded) {
              clearInterval(intervalId)
              resolve(list)
            }
          }, interval)
        } else {
          resolve(list)
        }
      })
    },
    async getVehicleList(opt) {
      const { orderBy, ...otherOpt } = opt
      const self = this
      const response = await this.$apollo.query({
        query: GET_VEHICLE_FILTER,
        variables: {
          filter: {
            status: ['I', 'R', 'A'],
            orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
            ...otherOpt,
          },
        },
      })
      let res = { ...response.data.vehiclesFilter.vehiclesFilterPaginated }
      res.data = res.data.map((item) => {
        let status = ''
        if (item.status === 'I') {
          status = self.$t('master.status.aktif')
        } else if (item.status === 'R') {
          status = self.$t('master.status.perbaikan')
        } else {
          status = self.$t('master.status.arsip')
        }
        return Object.assign({}, item, { statusString: status })
      })
      return res
    },
    filterStatus(key) {
      if (key == 'all') {
        this.motionStatus = this.vehicleStatusOptions.reduce((acc, item) => {
          acc[item.key] = false
          return acc
        }, {})
        this.motionStatus.all = true
      } else {
        this.motionStatus.all = false
        this.motionStatus[key] = !this.motionStatus[key]
        let obj = { ...this.motionStatus }
        let objArr = []
        Object.keys(obj).forEach(function (key) {
          if (obj[key]) {
            objArr.push(true)
          }
        })
        if (objArr.length === this.vehicleStatus.length || objArr.length === 0) {
          this.motionStatus = this.vehicleStatusOptions.reduce((acc, item) => {
            acc[item.key] = false
            return acc
          }, {})
          this.motionStatus.all = true
          return
        }
      }
      this.btnKey += 1
    },
    handleInput(value) {
      value.map((item) => {
        if (!item.values) {
          if (item.key === 'motionStatus') {
            item.values = this.vehicleStatus.map((item) => item.key)
          }
          if (item.key === 'vehicleId') {
            item.values = []
          }
        } else {
          if (item.key === 'motionStatus') {
            item.values = this.motionStatus.all
              ? this.vehicleStatus.map((item) => item.key)
              : this.vehicleStatus
                  .filter((item) => this.motionStatus[item.key])
                  .map((item) => item.key)
          }
        }
      })

      this.$emit('applyFilters', value)
      this.track('Terapkan Filter on Control Tower V2')
    },
    handleReset() {
      this.motionStatus = {
        all: true,
      }
      this.assignmentType = 'all'
      this.localFilters = {}
    },
    handleSetFilter(value) {
      if (!value) return

      this.convertMotionStatusArrayToObject(value.motionStatus)

      this.localFilters = {
        motionStatus: value.motionStatus || [],
        vehicleId: value.licensePlate || [],
        assignmentType: value.assignmentType || 'all',
      }
    },
    async handleSaveFilter(filters) {
      const input = {
        entityType: 'ControlTower',
        fieldType: 'Filter',
        label: filters.name,
        value: filters.value,
      }
      const res = await this.$apollo.mutate({
        mutation: CREATE_USER_PREFERENCES,
        variables: {
          input: input,
        },
      })
      if (!res.data.createUserPreference.isSuccessful) {
        this.$mcToast.error(res.data.createUserPreference.message)
        return
      }
      this.$mcToast.success(this.$t('controlTower.toast.saveFilterSuccess'))
      this.$apollo.queries.userPreferences.refetch()
      this.$bvModal.hide('modal-create-filter')
    },

    openModalCreate() {
      this.$bvModal.show('modal-create-filter')
    },

    updateSelectedRow(rows) {
      this.selectedRows = rows[0]
    },

    async handleUpdateFilter() {
      const input = {
        entityType: 'ControlTower',
        fieldType: 'Filter',
        label: this.selectedRows.label,
        data: {
          entityType: 'ControlTower',
          fieldType: 'Filter',
          label: this.selectedRows.label,
          value: this.selectedRows.value,
        },
      }

      const res = await this.$apollo.mutate({
        mutation: UPDATE_USER_PREFERENCES,
        variables: {
          input: input,
        },
      })
      if (!res.data.updateUserPreference.isSuccessful) {
        this.$mcToast.error(res.data.updateUserPreference.message)
        return
      }
      this.$mcToast.success(this.$t('controlTower.toast.editFilterSuccess'))
      this.$apollo.queries.userPreferences.refetch()
      this.$bvModal.hide('modal-edit-filter')
    },

    async handleDeleteFilter(label) {
      const input = {
        entityType: 'ControlTower',
        fieldType: 'Filter',
        label: label,
      }

      const res = await this.$apollo.mutate({
        mutation: DELETE_USER_PREFERENCES,
        variables: {
          input: input,
        },
      })

      if (!res.data.deleteUserPreference.isSuccessful) {
        this.$mcToast.error(res.data.deleteUserPreference.message)
        return
      }

      this.$mcToast.success(this.$t('controlTower.toast.deleteFilterSuccess'))
      this.$apollo.queries.userPreferences.refetch()
      this.$refs.tableFilter.showDeleteModal = false
    },
  },
}
</script>
